import { computed, inject, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
}

const initialState: PWAInstallState = {
  isInstallable: false,
  isInstalled: false,
  isStandalone: false,
  isIOS: false,
  isAndroid: false,
  installPrompt: null,
};

export const PWAInstallStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    installButtonText: computed(() => {
      if (store.isIOS()) return 'PWA.INSTALL_BUTTON';
      if (store.isAndroid()) return 'PWA.INSTALL_BUTTON';
      return 'PWA.INSTALL_BUTTON';
    }),

    installationStatus: computed(() => {
      if (store.isStandalone()) return 'standalone';
      if (store.isInstalled()) return 'installed';
      if (store.isInstallable()) return 'installable';
      return 'not-installable';
    }),
  })),

  withMethods((store, ngZone = inject(NgZone)) => ({
    init() {
      ngZone.run(() => {
        this.detectPlatform();
        this.detectStandaloneMode();
        this.setupInstallPromptListener();
        this.detectExistingInstallation();
      });

      if (Capacitor.isNativePlatform()) {
        patchState(store, { isInstallable: false });
        return;
      }

      if (store.isIOS()) {
        patchState(store, { isInstallable: !store.isStandalone() });
      }
    },

    detectPlatform() {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window);
      const isAndroid = /Android/.test(userAgent);

      patchState(store, { isIOS, isAndroid });
    },

    detectStandaloneMode() {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        ('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone) ||
        document.referrer.includes('android-app://');

      patchState(store, { isStandalone });
    },

    setupInstallPromptListener() {
      window.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        const installEvent = e as BeforeInstallPromptEvent;

        patchState(store, {
          isInstallable: true,
          installPrompt: installEvent,
        });
      });

      window.addEventListener('appinstalled', () => {
        patchState(store, {
          isInstalled: true,
          isInstallable: false,
          installPrompt: null,
        });
      });
    },

    detectExistingInstallation() {
      if (store.isStandalone()) {
        patchState(store, { isInstalled: true });
        return;
      }

      if (store.isAndroid() && document.referrer.includes('android-app://')) {
        patchState(store, { isInstalled: true });
      }
    },

    async promptInstall(): Promise<{ outcome: 'accepted' | 'dismissed' | 'not-available' }> {
      const installPrompt = store.installPrompt();

      if (!installPrompt) {
        return { outcome: 'not-available' };
      }

      try {
        await installPrompt.prompt();
        const result = await installPrompt.userChoice;

        if (result.outcome === 'accepted') {
          patchState(store, {
            isInstalled: true,
            isInstallable: false,
            installPrompt: null,
          });
        }

        return result;
      } catch (error) {
        console.error('Install prompt failed:', error);
        return { outcome: 'dismissed' };
      }
    },

    checkPWASupport(): {
      serviceWorker: boolean;
      manifest: boolean;
      installPrompt: boolean;
      standalone: boolean;
    } {
      return {
        serviceWorker: 'serviceWorker' in navigator,
        manifest: 'onbeforeinstallprompt' in window,
        installPrompt: 'onbeforeinstallprompt' in window,
        standalone: window.matchMedia('(display-mode: standalone)').matches,
      };
    },
  })),
);
